import { ChangeEvent, useCallback, useState } from "react";
import { PaginationItem, Theme, Tooltip, useMediaQuery } from "@mui/material";
import { Pagination } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { fetchReferralStatisticService } from "@/services/referral.service";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEY } from "@/constants";
import { BookingSortBy, EOrderSortBy } from "@/types";
import { fetchListBookingService } from "@/services/booking.service";
import useDebounce from "@/hooks/useDebounce";
import { tooltipStyles } from "@/utils/tooltipStyles";
import { truncateAddress } from "@/utils/truncateText";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import BookingPointCard from "./components/BookingPointCard";

const BookingPointsPage = () => {
  const [page, setPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<BookingSortBy>(
    BookingSortBy.TOTAL_BOOKING_POINTS,
  );
  const [sortOrder, setSortOrder] = useState<EOrderSortBy>(EOrderSortBy.DESC);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const [query, setQuery] = useState<string>("");
  const debouncedQuery = useDebounce(query, 500);
  const { data: bookings, isLoading } = useQuery({
    queryKey: [
      QUERY_KEY.fetchBookingPoints,
      page,
      debouncedQuery,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      fetchListBookingService({
        page,
        size: 10,
        sortBy,
        sortOrder,
        query: debouncedQuery,
      }),
  });

  const { data: statistic, isLoading: isLoadingStatistic } = useQuery({
    queryKey: [QUERY_KEY.fetchReferralStatistic],
    queryFn: () => fetchReferralStatisticService(),
  });

  const handleChangePage = useCallback(
    (_: ChangeEvent<unknown>, pageNext: number) => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setPage(pageNext);
    },
    [],
  );

  const handleSort = useCallback(
    (column: BookingSortBy) => {
      if (sortBy === column) {
        setSortOrder(
          sortOrder === EOrderSortBy.ASC ? EOrderSortBy.DESC : EOrderSortBy.ASC,
        );
      } else {
        setSortBy(column);
        setSortOrder(EOrderSortBy.DESC);
      }
    },
    [sortBy, sortOrder],
  );

  const count = bookings?.totalPages || 0;

  return (
    <>
      <div className="-mt-16 flex items-center gap-4 max-sm:mt-0 md:w-1/2">
        <h2 className="text-3xl font-semibold text-text max-sm:text-[18px]">
          Referral Points
        </h2>
      </div>

      <div className="mt-5">
        <div className="mb-6 max-sm:mb-4">
          <div className="flex h-12 w-1/2 items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white px-3 max-sm:w-full lg:min-w-[269px] lg:max-w-[269px]">
            <img
              src="/icons/search.svg"
              alt="search"
              className="aspect-square lg:size-5"
            />
            <input
              placeholder="Search by wallet address"
              onChange={(e) => setQuery(e.target.value)}
              inputMode="search"
              value={query}
              className="w-full bg-white text-sm font-normal text-text outline-none placeholder:text-[#94A3B8]"
            />
          </div>
        </div>

        <div className="mb-6 grid grid-cols-3 gap-[26px] max-sm:mb-4 max-sm:gap-[6px]">
          <div className="flex items-center rounded-lg border border-[#E2E8F0] bg-[#F6F9FB] p-4 shadow-sm max-sm:flex-col max-sm:items-start max-sm:gap-1 max-sm:bg-white max-sm:px-3">
            {!isMobile ? (
              <img
                src="/icons/trailing-slot.svg"
                alt="hand-coins"
                className="mr-3"
              />
            ) : (
              <img
                src="/icons/coins-hand.svg"
                alt="hand-coins"
                className="p-1"
              />
            )}

            <div className="max-sm:space-y-1">
              <span className="block text-[16px] font-medium text-text max-sm:text-[12px]">
                Points Earned
              </span>
              <div className="text-[16px] text-[#334155]">
                {isLoadingStatistic ? (
                  <div className="h-6 w-16 animate-pulse rounded bg-neutral-400" />
                ) : (
                  statistic?.totalEarnedPoints || 0
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center rounded-lg border border-[#E2E8F0] bg-[#F6F9FB] p-4 shadow-sm max-sm:flex-col max-sm:items-start max-sm:gap-1 max-sm:bg-white max-sm:px-3">
            {!isMobile ? (
              <img src="/icons/star.svg" alt="star" className="mr-3" />
            ) : (
              <img src="/icons/star-01.svg" alt="star" className="p-1" />
            )}
            <div className="max-sm:space-y-1">
              <span className="block text-[16px] font-medium text-text max-sm:text-[12px]">
                Available Points
              </span>
              <div className="text-[16px] text-[#334155]">
                {isLoadingStatistic ? (
                  <div className="h-6 w-16 animate-pulse rounded bg-neutral-400" />
                ) : (
                  statistic?.totalPoints || 0
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center rounded-lg border border-[#E2E8F0] bg-[#F6F9FB] p-4 shadow-sm max-sm:flex-col max-sm:items-start max-sm:gap-1 max-sm:bg-white max-sm:px-3">
            {!isMobile ? (
              <img
                src="/icons/check-circle.svg"
                alt="check-circle"
                className="mr-3"
              />
            ) : (
              <img
                src="/icons/check-circle-broken.svg"
                alt="check-circle-broken"
                className="p-1"
              />
            )}

            <div className="max-sm:space-y-1">
              <span className="block text-[16px] font-medium text-text max-sm:text-[12px]">
                Points Used
              </span>
              <div className="text-[16px] text-[#334155]">
                {isLoadingStatistic ? (
                  <div className="h-6 w-16 animate-pulse rounded bg-neutral-400" />
                ) : (
                  statistic?.totalClaimedPoints || 0
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-md bg-white shadow-sm max-sm:hidden">
          <div className="grid grid-cols-5 bg-indigo-600 text-white">
            <div className="flex items-center px-4 py-3 text-sm font-medium">
              Wallet Address
            </div>
            <div
              className="flex cursor-pointer items-center justify-center gap-1 px-4 py-3 text-sm font-medium"
              onClick={() => handleSort(BookingSortBy.TOTAL_BOOKING_POINTS)}
            >
              Points Earned
              <img src={`/icons/chevron-selector-vertical.svg`} alt="arrow" />
            </div>
            <div
              className="flex cursor-pointer items-center justify-center gap-1 px-4 py-3 text-sm font-medium"
              onClick={() => handleSort(BookingSortBy.TOTAL_REFERRAL_POINTS)}
            >
              Referral Points Earned
              <img src={`/icons/chevron-selector-vertical.svg`} alt="arrow" />
            </div>
            <div
              className="flex cursor-pointer items-center justify-center gap-1 px-4 py-3 text-sm font-medium"
              onClick={() => handleSort(BookingSortBy.CLAIMED_POINTS)}
            >
              Points Used
              <img src={`/icons/chevron-selector-vertical.svg`} alt="arrow" />
            </div>
            <div
              className="flex cursor-pointer items-center justify-center gap-1 px-4 py-3 text-sm font-medium"
              onClick={() => handleSort(BookingSortBy.AVAILABLE_POINTS)}
            >
              Points Available
              <img src={`/icons/chevron-selector-vertical.svg`} alt="arrow" />
            </div>
          </div>

          <div>
            {isLoading ? (
              Array(5)
                .fill(null)
                .map((_, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-5 border-b border-[#E2E8F0] hover:bg-[#E2E8F0]"
                  >
                    <div className="px-4 py-6">
                      <div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
                    </div>
                    <div className="px-4 py-6">
                      <div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
                    </div>
                    <div className="px-4 py-6">
                      <div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
                    </div>
                    <div className="px-4 py-6">
                      <div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
                    </div>
                    <div className="px-4 py-6">
                      <div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
                    </div>
                  </div>
                ))
            ) : bookings?.data.length === 0 ? (
              <div className="flex flex-col items-center py-6">
                <FolderOffOutlinedIcon fontSize="large" className="text-gray" />
                <p className="mt-2 font-medium text-gray">Data not found</p>
              </div>
            ) : (
              bookings?.data.map((booking, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-5 ${index === bookings?.data.length - 1 ? "" : "border-b border-[#E2E8F0]"} hover:bg-[#E2E8F0]`}
                >
                  <div className="flex items-center px-4 py-6 text-sm text-text">
                    {(() => {
                      if (booking.walletAddress.length > 15) {
                        return (
                          <Tooltip
                            title={booking.walletAddress}
                            placement="right"
                            arrow
                            componentsProps={{
                              tooltip: { sx: tooltipStyles.tooltip },
                              arrow: { sx: tooltipStyles.arrow },
                            }}
                          >
                            <span className="line-clamp-1 flex w-[172px] items-center font-medium text-text">
                              {truncateAddress(booking.walletAddress)}
                            </span>
                          </Tooltip>
                        );
                      }
                      return (
                        <span className="line-clamp-1 flex w-[172px] items-center font-medium text-text">
                          {booking.walletAddress}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="flex items-center justify-center px-4 py-6 text-center text-sm text-text">
                    <span className="text-[16px] font-medium text-text">
                      {booking.totalBookingPoints}{" "}
                      {booking.totalBookingPoints < 2 ? "Point" : "Points"}
                    </span>
                  </div>
                  <div className="flex items-center justify-center px-4 py-6 text-center text-sm text-text">
                    <span className="text-[16px] font-medium text-text">
                      {booking.totalReferralPoints}{" "}
                      {booking.totalReferralPoints < 2 ? "Point" : "Points"}
                    </span>
                  </div>
                  <div className="flex items-center justify-center px-4 py-6 text-center text-sm text-text">
                    <span className="text-[16px] font-medium text-text">
                      {booking.totalClaimedPoints}{" "}
                      {booking.totalClaimedPoints < 2 ? "Point" : "Points"}
                    </span>
                  </div>
                  <div className="flex items-center justify-center px-4 py-6 text-center text-sm font-bold text-text">
                    <span className="text-[16px] font-medium text-text">
                      {booking.totalAvailablePoints}{" "}
                      {booking.totalAvailablePoints < 2 ? "Point" : "Points"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {!!count && (
            <div className="flex justify-center py-4">
              <Pagination
                shape="rounded"
                count={count}
                page={page}
                onChange={handleChangePage}
                siblingCount={1}
                boundaryCount={1}
                renderItem={(item) => (
                  <PaginationItem
                    slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                    sx={{
                      backgroundColor: "transparent",
                      color: "#94A3B8",
                      fontSize: 16,
                      "&.Mui-selected": {
                        backgroundColor: "#E0E7FF",
                        color: "#4F46E5",
                        fontWeight: "bold",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.5,
                      },
                    }}
                    {...item}
                  />
                )}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:hidden">
          {isLoading ? (
            Array(5)
              .fill(null)
              .map((_, idx) => (
                <div
                  key={idx}
                  className="mb-4 rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm"
                >
                  <div className="mb-2 flex justify-between text-xs font-medium text-[#64748B]">
                    <span>Customer ID</span>
                    <span className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
                  </div>
                  <div className="mb-2 flex justify-between text-xs font-medium text-[#64748B]">
                    <span>Points Earned</span>
                    <span className="h-4 w-16 animate-pulse rounded bg-neutral-200" />
                  </div>
                  <div className="mb-2 flex justify-between text-xs font-medium text-[#64748B]">
                    <span>Referral Points Earned</span>
                    <span className="h-4 w-12 animate-pulse rounded bg-neutral-200" />
                  </div>
                  <div className="mb-2 flex justify-between text-xs font-medium text-[#64748B]">
                    <span>Points Used</span>
                    <span className="h-4 w-16 animate-pulse rounded bg-neutral-200" />
                  </div>
                  <div className="flex justify-between text-xs font-medium text-[#64748B]">
                    <span>Points Available</span>
                    <span className="h-4 w-16 animate-pulse rounded bg-neutral-200" />
                  </div>
                </div>
              ))
          ) : bookings?.data.length === 0 ? (
            <div className="flex flex-col items-center py-6">
              <FolderOffOutlinedIcon fontSize="large" className="text-gray" />
              <p className="mt-2 font-medium text-gray">Data not found</p>
            </div>
          ) : (
            bookings?.data.map((booking, index) => (
              <BookingPointCard key={index} booking={booking} />
            ))
          )}

          {!!count && (
            <div className="flex justify-center py-4">
              <Pagination
                shape="rounded"
                count={count}
                page={page}
                onChange={handleChangePage}
                siblingCount={1}
                boundaryCount={1}
                renderItem={(item) => (
                  <PaginationItem
                    slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                    sx={{
                      backgroundColor: "transparent",
                      color: "#94A3B8",
                      fontSize: 16,
                      "&.Mui-selected": {
                        backgroundColor: "#E0E7FF",
                        color: "#4F46E5",
                        fontWeight: "bold",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.5,
                      },
                    }}
                    {...item}
                  />
                )}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BookingPointsPage;
